import { DialogConfig } from '../../../services/network';
import { escapeHtml } from '../../../shared/html';

/** Which contact fields the dialog asks for, plus the privacy notice (GDPR) shown to the visitor. */
export interface ContactSettings {
    askName: boolean;
    askEmail: boolean;
    askPhone: boolean;
    privacyNoticeText: string;
}

export interface ContactDetailsInput {
    name?: string;
    email?: string;
    phone?: string;
    consent: boolean;
}

export interface ContactReadResult {
    contact?: ContactDetailsInput;
    error?: string;
}

/** Returns null when the dialog does not collect contact details. */
export function getContactSettings(config: DialogConfig | null): ContactSettings | null {
    if (!config?.collectContactDetails || !config.privacyNoticeText) return null;
    if (!config.contactAskName && !config.contactAskEmail && !config.contactAskPhone) return null;

    return {
        askName: config.contactAskName,
        askEmail: config.contactAskEmail,
        askPhone: config.contactAskPhone,
        privacyNoticeText: config.privacyNoticeText
    };
}

/**
 * Optional section: the fields are hidden until the visitor chooses to leave contact details.
 * idPrefix keeps the element ids unique when the section is used in several places on the page.
 */
export function renderContactSection(settings: ContactSettings, idPrefix: string): string {
    const field = (name: string, label: string, type: string, autocomplete: string, maxLength: number) => `
        <div class="mb-2">
            <label for="${idPrefix}Contact${name}" class="form-label small mb-1">${label}</label>
            <input type="${type}" class="form-control form-control-sm" id="${idPrefix}Contact${name}" autocomplete="${autocomplete}" maxlength="${maxLength}" />
        </div>`;

    return `
        <div class="contact-section border-top pt-2 mt-2" data-contact-prefix="${idPrefix}">
            <div class="form-check">
                <input class="form-check-input" type="checkbox" id="${idPrefix}ContactToggle" />
                <label class="form-check-label" for="${idPrefix}ContactToggle">
                    Jag vill lämna kontaktuppgifter <span class="text-muted">(frivilligt)</span>
                </label>
            </div>
            <div id="${idPrefix}ContactFields" class="d-none mt-2">
                ${settings.askName ? field('Name', 'Namn', 'text', 'name', 200) : ''}
                ${settings.askEmail ? field('Email', 'E-post', 'email', 'email', 256) : ''}
                ${settings.askPhone ? field('Phone', 'Telefon', 'tel', 'tel', 50) : ''}
                <div class="small text-muted border rounded p-2 mb-2 contact-privacy-notice">${escapeHtml(settings.privacyNoticeText)}</div>
                <div class="form-check">
                    <input class="form-check-input" type="checkbox" id="${idPrefix}ContactConsent" />
                    <label class="form-check-label small" for="${idPrefix}ContactConsent">
                        Jag har läst informationen och godkänner att mina kontaktuppgifter sparas.
                    </label>
                </div>
            </div>
        </div>
    `;
}

/** Shows/hides the fields when the visitor ticks "leave contact details". */
export function initContactSection(idPrefix: string): void {
    const toggle = document.getElementById(`${idPrefix}ContactToggle`) as HTMLInputElement | null;
    toggle?.addEventListener('change', () => setFieldsVisible(idPrefix, toggle.checked));
}

export function resetContactSection(idPrefix: string): void {
    const toggle = document.getElementById(`${idPrefix}ContactToggle`) as HTMLInputElement | null;
    if (toggle) toggle.checked = false;
    ['Name', 'Email', 'Phone'].forEach(name => {
        const input = document.getElementById(`${idPrefix}Contact${name}`) as HTMLInputElement | null;
        if (input) input.value = '';
    });
    const consent = document.getElementById(`${idPrefix}ContactConsent`) as HTMLInputElement | null;
    if (consent) consent.checked = false;
    setFieldsVisible(idPrefix, false);
}

/**
 * Reads and validates the section. Returns no contact when the visitor has not chosen to leave details
 * or has left the fields empty - contact details are never required.
 */
export function readContactSection(idPrefix: string): ContactReadResult {
    const toggle = document.getElementById(`${idPrefix}ContactToggle`) as HTMLInputElement | null;
    if (!toggle?.checked) return {};

    const value = (name: string) =>
        (document.getElementById(`${idPrefix}Contact${name}`) as HTMLInputElement | null)?.value.trim() || undefined;

    const name = value('Name');
    const email = value('Email');
    const phone = value('Phone');
    if (!name && !email && !phone) return {};

    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        return { error: 'E-postadressen är inte giltig.' };
    }

    if (phone && (!/^\+?[0-9\s\-()]+$/.test(phone) || phone.replace(/\D/g, '').length < 5)) {
        return { error: 'Telefonnumret är inte giltigt.' };
    }

    const consent = (document.getElementById(`${idPrefix}ContactConsent`) as HTMLInputElement | null)?.checked ?? false;
    if (!consent) {
        return { error: 'Du måste godkänna hur kontaktuppgifterna behandlas, eller avmarkera "Jag vill lämna kontaktuppgifter".' };
    }

    return { contact: { name, email, phone, consent } };
}

function setFieldsVisible(idPrefix: string, visible: boolean): void {
    document.getElementById(`${idPrefix}ContactFields`)?.classList.toggle('d-none', !visible);
}
